import { useCallback } from 'react'
import { type NavigateOptions, type To, useNavigate } from 'react-router'

import { useLocale } from './LocaleProvider'
import { localizeUrl, stripLocaleFromPath } from './utils'


/**
 * A wrapper around useNavigate that automatically adds the locale prefix
 */
export function useLocalizedNavigate () {
  const navigate = useNavigate()
  const { locale } = useLocale()

  const localizedNavigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      // Handle numeric navigation (back/forward)
      if (typeof to === 'number') {
        navigate(to)
        return
      }

      // Handle string paths
      if (typeof to === 'string') {
        // External URLs or hash links - pass through
        if (
          to.startsWith('http://') ||
          to.startsWith('https://') ||
          to.startsWith('//') ||
          to.startsWith('#')
        ) {
          navigate(to, options)
          return
        }

        // Localize the path
        const cleanPath = stripLocaleFromPath(to)
        const localizedPath = localizeUrl(cleanPath, locale)
        navigate(localizedPath, options)
        return
      }

      // Handle object navigation { pathname, search, hash }
      if (typeof to === 'object' && to.pathname) {
        const cleanPath = stripLocaleFromPath(to.pathname)
        const localizedPath = localizeUrl(cleanPath, locale)

        // Extract pathname from localized URL
        const url = new URL(localizedPath, 'http://localhost')

        navigate(
          {
            ...to,
            pathname: url.pathname,
          },
          options,
        )
        return
      }

      // Fallback
      navigate(to, options)
    },
    [ navigate, locale ],
  )

  return localizedNavigate
}
